var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { differenceInMinutes, format } from "date-fns";
import { PatientDataVisualizationPdfStatus } from "healthex-common/patient.data.visualization.dtos";
import { buildWebsiteUrl, WebsiteURL } from "healthex-common/websiteurls";
import { computed, observable } from "mobx";
import { Disposer } from "../Infra/Disposer";
import { getObservableT, i18nGetCurrentLanguage, i18nGetDateFnsLocale } from "../Infra/i18next";
import { NOT_LOADED } from "../Infra/RemoteData";
import { NamedRoutes } from "./NamedRoutes";
import { openAsyncWindow } from "./WindowHelpers";
const t = getObservableT("patients");
export class PatientMyHealthExViewModel {
    constructor(container) {
        this.disposer = new Disposer();
        this.supportDialogOpen = false;
        this.environment = container.environment;
        this.authManager = container.authManager;
        this.appUIManager = container.appUIManager;
        this.routingUIManager = container.routingUIManager;
        this.patientPreferencesManager = container.patientPreferencesManager;
        this.consentConfigManager = container.consentConfigManager;
        this.embeddedManager = container.embeddedManager;
        this.dashboardUIManager = container.patientResearchDashboardUIManager;
    }
    run() { }
    stop() {
        this.disposer.dispose();
    }
    get name() {
        return this.authManager.name;
    }
    get isUnifiedRecordDownloadable() {
        return this.dashboardUIManager.isUnifiedRecordDownloadable;
    }
    get mostRecentRetrieval() {
        return this.dashboardUIManager.mostRecentRetrieval;
    }
    get hasUnifiedRecordExpired() {
        return this.dashboardUIManager.hasUnifiedRecordExpired;
    }
    get isUnifiedRecordProcessing() {
        return this.dashboardUIManager.isUnifiedRecordProcessing;
    }
    get unifiedRecordSummary() {
        if (this.hasUnifiedRecordExpired === true)
            return null;
        return this.dashboardUIManager.unifiedRecordSummary;
    }
    get recordsLastAccessed() {
        if (this.unifiedRecordSummary === NOT_LOADED || !this.unifiedRecordSummary)
            return null;
        const lastAccessed = this.unifiedRecordSummary.createdAt;
        const now = new Date();
        const diffInMinutes = differenceInMinutes(now, lastAccessed);
        const diffInHours = Math.floor(diffInMinutes / 60);
        const diffInDays = Math.floor(diffInMinutes / (60 * 24));
        const diffInWeeks = Math.floor(diffInDays / 7);
        let timeAgo;
        if (diffInMinutes < 1) {
            timeAgo = t("just now");
        }
        else if (diffInMinutes < 60) {
            timeAgo = t("less than an hour ago");
        }
        else if (diffInHours < 24) {
            timeAgo = t("{{ hours }} ago", { hours: t("hoursWithCount", { count: diffInHours }) });
        }
        else if (diffInHours < 48) {
            timeAgo = t("yesterday");
        }
        else if (diffInDays < 7) {
            timeAgo = t("{{ days }} ago", { days: t("daysWithCount", { count: diffInDays }) });
        }
        else if (diffInDays < 30) {
            timeAgo = t("{{ weeks }} ago", { weeks: t("weeksWithCount", { count: diffInWeeks }) });
        }
        else {
            timeAgo = t("on {{ date }}", {
                date: format(lastAccessed, "MMM d, yyyy", { locale: i18nGetDateFnsLocale() }),
            });
        }
        return t("Records accessed {{ timeAgo }}", { timeAgo });
    }
    get createdAt() {
        var _a;
        const createdAt = new Date((_a = this.authManager.createdAt) !== null && _a !== void 0 ? _a : new Date());
        return format(createdAt, "LLLL yyyy", { locale: i18nGetDateFnsLocale() });
    }
    get showDataSettings() {
        var _a, _b;
        if (this.patientPreferencesManager.preferences.value === NOT_LOADED)
            return NOT_LOADED;
        if (this.consentConfigManager.consentConfig.value === NOT_LOADED)
            return NOT_LOADED;
        return !((_b = (_a = this.patientPreferencesManager.preferences.value) === null || _a === void 0 ? void 0 : _a.optedOut) !== null && _b !== void 0 ? _b : false);
    }
    get showLogout() {
        return !this.embeddedManager.embedded;
    }
    get primaryCallToAction() {
        if (this.hasUnifiedRecordExpired === true || this.isUnifiedRecordProcessing === true) {
            return { label: t("Get my latest records"), onClick: () => this.getUpdatedRecords() };
        }
        else if (this.isUnifiedRecordDownloadable && this.shouldShowUnifiedRecordButton) {
            return {
                label: t("See my HealthEx Unified Record"),
                longPressLabel: t("See my raw records"),
                onClick: (rawRecordsMode) => this.showUnifiedRecord(rawRecordsMode ? "raw" : "pdf"),
            };
        }
        return { label: t("Get my HealthEx Unified Record"), onClick: () => this.getRecords() };
    }
    get secondaryCallToAction() {
        if (this.hasUnifiedRecordExpired === true || this.isUnifiedRecordProcessing === true) {
            return null;
        }
        else if (this.isUnifiedRecordDownloadable) {
            return { label: t("Get my latest records"), onClick: () => this.getUpdatedRecords() };
        }
        return null;
    }
    get isPrimaryCallToActionDisabled() {
        return this.isUnifiedRecordProcessing === true;
    }
    get shouldShowUnifiedRecordButton() {
        if (this.mostRecentRetrieval === NOT_LOADED)
            return false;
        if (!this.mostRecentRetrieval)
            return false;
        const retrieval = this.mostRecentRetrieval;
        if (!retrieval.finalized) {
            return false;
        }
        if (retrieval.finalized && retrieval.pdfStatus !== PatientDataVisualizationPdfStatus.COMPLETED) {
            return false;
        }
        return true;
    }
    showUnifiedRecord(type = "pdf") {
        void openAsyncWindow(async () => {
            var _a, _b;
            const url = type === "raw"
                ? (_a = (await this.dashboardUIManager.getDownloadRawDocumentsLink())) === null || _a === void 0 ? void 0 : _a.downloadLink
                : (_b = (await this.dashboardUIManager.getDownloadPdfLink())) === null || _b === void 0 ? void 0 : _b.downloadLink;
            if (!url) {
                console.warn(`No URL available`);
                return null;
            }
            return url;
        });
    }
    getRecords() {
        this.routingUIManager.navigateTo(NamedRoutes.PATIENT_IAS_CONSENT);
    }
    getUpdatedRecords() {
        this.routingUIManager.navigateTo(NamedRoutes.PATIENT_IAS_DATA_UPDATE);
    }
    goToManageProfile() {
        this.routingUIManager.navigateTo(NamedRoutes.PATIENT_MY_HEALTHEX_PROFILE);
    }
    goToHowDataIsManaged() {
        window.open(buildWebsiteUrl(WebsiteURL.HOW_HEALTHEX_MANAGES_DATA, this.environment.WEBSITE_BASE_URL, i18nGetCurrentLanguage()));
    }
    goToSupport() {
        window.open(buildWebsiteUrl(WebsiteURL.SUPPORT, this.environment.WEBSITE_BASE_URL, i18nGetCurrentLanguage()));
    }
    showSupportDialog() {
        this.supportDialogOpen = true;
    }
    closeSupportDialog() {
        this.supportDialogOpen = false;
    }
    goToPrivacyPolicy() {
        window.open(buildWebsiteUrl(WebsiteURL.PRIVACY, this.environment.WEBSITE_BASE_URL, i18nGetCurrentLanguage()));
    }
    goToTermsAndConditions() {
        window.open(buildWebsiteUrl(WebsiteURL.TERMS, this.environment.WEBSITE_BASE_URL, i18nGetCurrentLanguage()));
    }
    async logout() {
        await this.appUIManager.logout();
    }
}
__decorate([
    observable,
    __metadata("design:type", Object)
], PatientMyHealthExViewModel.prototype, "supportDialogOpen", void 0);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientMyHealthExViewModel.prototype, "name", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientMyHealthExViewModel.prototype, "isUnifiedRecordDownloadable", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientMyHealthExViewModel.prototype, "mostRecentRetrieval", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientMyHealthExViewModel.prototype, "hasUnifiedRecordExpired", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientMyHealthExViewModel.prototype, "isUnifiedRecordProcessing", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientMyHealthExViewModel.prototype, "unifiedRecordSummary", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientMyHealthExViewModel.prototype, "recordsLastAccessed", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientMyHealthExViewModel.prototype, "createdAt", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientMyHealthExViewModel.prototype, "showDataSettings", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientMyHealthExViewModel.prototype, "showLogout", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientMyHealthExViewModel.prototype, "primaryCallToAction", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientMyHealthExViewModel.prototype, "secondaryCallToAction", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientMyHealthExViewModel.prototype, "isPrimaryCallToActionDisabled", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientMyHealthExViewModel.prototype, "shouldShowUnifiedRecordButton", null);
